import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, IMAGES } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitForgot = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data.message);
      setForgotOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      setUser(data);
      toast.success(mode === "login" ? `Bienvenue, ${data.first_name} !` : "Compte créé avec succès !");
      navigate(["admin","super_admin","staff"].includes(data.role) ? "/admin" : "/profil");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profil";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <div className="text-center mb-10">
        <img src={IMAGES.logo} alt="Italian Club" className="h-20 w-20 object-contain rounded-full bg-white/95 p-1 mx-auto mb-4" />
        <h1 className="font-serif-elegant text-4xl font-bold">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>
        <p className="text-sm text-gray-400 mt-2">Accédez à vos réservations et à votre espace client</p>
      </div>
      <div className="flex rounded-full border border-white/15 p-1 mb-8">
        <button data-testid="login-tab" onClick={() => setMode("login")} className={`flex-1 py-2 rounded-full text-sm transition-colors duration-200 ${mode === "login" ? "bg-[#D4AF37] text-black font-medium" : "text-gray-300"}`}>
          Connexion
        </button>
        <button data-testid="register-tab" onClick={() => setMode("register")} className={`flex-1 py-2 rounded-full text-sm transition-colors duration-200 ${mode === "register" ? "bg-[#D4AF37] text-black font-medium" : "text-gray-300"}`}>
          Inscription
        </button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="register-firstname" required className="ic-input" placeholder="Prénom *" value={form.first_name} onChange={set("first_name")} />
              <input data-testid="register-lastname" required className="ic-input" placeholder="Nom *" value={form.last_name} onChange={set("last_name")} />
            </div>
            <input data-testid="register-phone" type="tel" className="ic-input" placeholder="Téléphone" value={form.phone} onChange={set("phone")} />
          </>
        )}
        <input data-testid="auth-email" required type="email" className="ic-input" placeholder="Email *" value={form.email} onChange={set("email")} />
        <input data-testid="auth-password" required type="password" className="ic-input" placeholder="Mot de passe *" value={form.password} onChange={set("password")} />
        {mode === "login" && (
          <div className="text-right">
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotOpen(!forgotOpen); setForgotEmail(form.email); }} className="text-xs text-[#D4AF37] hover:underline">
              Mot de passe oublié ?
            </button>
          </div>
        )}
        <button type="submit" disabled={submitting} data-testid="auth-submit-btn" className="btn-gold w-full !py-3.5 disabled:opacity-50">
          {submitting ? "Un instant…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
        </button>
      </form>
      {forgotOpen && mode === "login" && (
        <form onSubmit={submitForgot} className="mt-6 ic-card !transform-none p-5 space-y-3" data-testid="forgot-password-form">
          <p className="text-sm text-gray-300">Entrez votre email — nous vous enverrons un lien de réinitialisation.</p>
          <input data-testid="forgot-password-email" required type="email" className="ic-input" placeholder="Votre email *" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
          <button type="submit" disabled={submitting} data-testid="forgot-password-submit" className="btn-outline-gold w-full !py-2.5 text-sm disabled:opacity-50">
            Envoyer le lien de réinitialisation
          </button>
        </form>
      )}
      <div className="flex items-center gap-4 my-8">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-gray-500 uppercase tracking-widest">ou</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <button onClick={googleLogin} data-testid="google-login-btn" className="btn-outline-gold w-full !py-3.5">
        <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        Continuer avec Google
      </button>
    </div>
  );
}
